// Category-page URL helpers.
//
// Category pages live under `/product/<seg>/<seg>/...` where the path
// segments encode the full menu hierarchy (brand + menuLevel1/2/3) so a
// category that exists under multiple parents (e.g. "Bracelets" under
// brand "Cartier" AND a Product Range group) gets a unique, shareable URL.
//
// `/product/all` remains the special "shop everything" entry.

// Slugify a single segment: lowercase, trim, expand "&" to "and", then
// collapse every run of non-alphanumeric characters into single hyphens.
// The result is plain [a-z0-9-], so no percent-encoding is needed — an
// "&" never becomes "%26".
export const slugify = (value) =>
  String(value)
    .trim()
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

// De-slugify a single segment back to a human-readable name: percent-decode
// then turn hyphens back into spaces.
const deslugify = (segment) => {
  let decoded = segment;
  try {
    decoded = decodeURIComponent(segment);
  } catch {
    // Leave the raw segment if it isn't valid percent-encoding.
  }
  return decoded.replace(/-/g, " ");
};

/**
 * Build a hierarchical category path from a filter context.
 * @param {Object} ctx - optional brandName, menuLevel1, menuLevel2, menuLevel3
 * @returns {string} e.g. "/product/cartier/bracelets" or "/product/all"
 */
export const buildCategoryPath = (ctx = {}) => {
  const { brandName, menuLevel1, menuLevel2, menuLevel3 } = ctx || {};
  const segments = [brandName, menuLevel1, menuLevel2, menuLevel3]
    .filter((seg) => seg != null && String(seg).trim() !== "")
    .map(slugify);

  if (segments.length === 0) return "/product/all";
  return `/product/${segments.join("/")}`;
};

/**
 * Parse the splat after `/product/` into de-slugified segment names.
 * @param {string} splat - e.g. "cartier/bracelets"
 * @returns {string[]} e.g. ["cartier", "bracelets"]; [] for empty/"all"
 */
export const parseCategoryPath = (splat) => {
  if (!splat) return [];
  const trimmed = String(splat).replace(/^\/+|\/+$/g, "");
  if (!trimmed || trimmed.toLowerCase() === "all") return [];
  return trimmed
    .split("/")
    .filter((seg) => seg !== "")
    .map(deslugify);
};

/**
 * Normalise a hand-entered internal "/product/..." path so each segment is a
 * clean slug. Fixes copy-pasted URLs that still carry spaces, "&" or %-encoding
 * (e.g. /product/van-cleef-%26-arpels -> /product/van-cleef-and-arpels). Any
 * non-/product path is returned unchanged.
 */
export const normalizeProductPath = (path) => {
  if (typeof path !== "string") return path;
  const [base, query = ""] = path.split("?");
  if (!/^\/?product\//i.test(base)) return path;
  const rest = base.replace(/^\/?product\//i, "");
  const clean = rest
    .split("/")
    .filter(Boolean)
    .map((seg) => {
      let decoded = seg;
      try { decoded = decodeURIComponent(seg); } catch { /* keep raw */ }
      return slugify(decoded.replace(/-/g, " "));
    })
    .filter(Boolean)
    .join("/");
  const out = clean ? `/product/${clean}` : "/product/all";
  return query ? `${out}?${query}` : out;
};
